import lz from "lzutf8";
import { Page } from "@/types/Page";
import { Field } from "@/types/Field";
import { WebsiteThemeDefinition, WebsiteThemeId, WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import type { DirectionId } from "@/lib/designDirections/directions";
import { RegistrationType } from "@/types/enums/RegistrationType";
import {
  INJECTABLE_FIELD_DATA_TYPES,
  getInjectableRegistrationFields,
} from "@/lib/website/registrationFieldCatalog";
import {
  toCheckboxItems,
  type RegistrationConsent,
} from "@/lib/website/registrationConsents";
import {
  PROGRAM_BINDING_KEY,
  SPEAKERS_BINDING_KEY,
} from "@/lib/website-builder/eventBinding";
import { appbar, footer, agenda, contact, register, login, fi, acceptanceFi, ACCEPTANCE_VISIBILITY_FILTER, type TranslationFn } from "./temp";
import { webinar, webinarAppbar, webinarCustomCss, webinarFooter } from "./event-templates/webinar";
import { speaker, speakerAppbar, speakerFooter, speakerPreset } from "./event-templates/speaker";
import { charity, charityAppbar, charityFooter, charityPreset } from "./event-templates/charity";
import { workshop, workshopAppbar, workshopFooter, workshopPreset } from "./event-templates/workshop";
import { corporate, corporateAppbar, corporateFooter, corporatePreset } from "./event-templates/corporate";
import { dinner, dinnerAppbar, dinnerFooter, dinnerPreset } from "./event-templates/dinner";
import { networking, networkingAppbar, networkingFooter, networkingPreset } from "./event-templates/networking";
import { networkingNight, networkingNightAppbar, networkingNightFooter, networkingNightPreset } from "./event-templates/networkingNight";
import { summit, summitAppbar, summitFooter, summitPreset } from "./event-templates/summit";
import { soiree, soireeAppbar, soireeFooter, soireePreset } from "./event-templates/soiree";
import { press, pressAppbar, pressFooter, pressPreset } from "./event-templates/press";
import { gala, galaAppbar, galaFooter, galaPreset } from "./event-templates/gala";
import { productLaunch, productLaunchAppbar, productLaunchFooter, productLaunchPreset } from "./event-templates/productLaunch";

const A = import.meta.env.VITE_ASSET_BASE_URL;

// ── Theme style presets ──────────────────────────────────────────────

// ─── 1. MODERN ─ Universal default ──────────────────────────────────
// Theme-neutral baseline for the persisted theme-fields diff (ConfiguratorPanel).
// Decoupled from the theme list so it survives the legacy-theme removal.
export const BASE_THEME_DEFAULTS: WebsiteThemeStylePreset = {
  heroImage: `${A}/shared/hero-modern.jpg`,
  logoSrc: `${A}/shared/logo-modern.svg`,
  colorPalette: { primary: "#18181B", secondary: "#71717A", background: "#FFFFFF" },
  fonts: { header: "system-ui, -apple-system, 'Segoe UI', sans-serif", body: "Inter, system-ui, sans-serif" },
  rounding: "rounded-lg",
  recommendedSize: "large",
  bodyTheme: { fontColor: "#71717A", fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    h1: { fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif", fontSize: "36px", fontColor: "#18181B" },
    h2: { fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif", fontSize: "22px", fontColor: "#18181B" },
  },
  buttonsTheme: {
    primary: {
      bgColor: "#18181B", bgHoverColor: "#3F3F46", borderColor: "#FFFFFF", borderHoverColor: "#FFFFFF",
      fontColor: "#FFFFFF", fontHoverColor: "#FFFFFF", fontSize: "16px", fontHoverSize: "16px",
      borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg",
    },
    secondary: {
      bgColor: "transparent", bgHoverColor: "#FFFFFF", borderColor: "#FFFFFF", borderHoverColor: "#FFFFFF",
      fontColor: "#FFFFFF", fontHoverColor: "#18181B", fontSize: "16px", fontHoverSize: "16px",
      borderRadius: "rounded-lg", hoverBorderRadius: "rounded-lg",
    },
  },
  hyperlinkTheme: { fontColor: "#3F3F46", fontHoverColor: "#18181B", underline: false },
  formInputTheme: {
    labelFontColor: "#71717A", inputFontColor: "#3F3F46", backgroundColor: "#FFFFFF",
    border: true, borderColor: "#A1A1AA", borderRadius: "rounded-lg", borderStyle: "border-solid",
    borderWidth: "border", borderSides: [], fontFamily: "Inter, system-ui, sans-serif",
    fontSize: "text-sm", errorColor: "#EF4444",
  },
};

// ─── Event template: WEBINAR ─ Corporate-pro (cream + blue accent) ──
const WEBINAR_PRESET: WebsiteThemeStylePreset = {
  heroImage: `${A}/shared/templates/01-webinar/hero-einladung.jpg`,
  logoSrc: `${A}/shared/logo.svg`,
  colorPalette: { primary: "#1E40AF", secondary: "#475569", background: "#F7F2ED" },
  fonts: { header: "Georgia, 'Times New Roman', serif", body: "Inter, 'Helvetica Neue', Arial, sans-serif" },
  rounding: "rounded-none",
  recommendedSize: "large",
  bodyTheme: { fontColor: "#475569", fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    // WS4-B2 display bump: text-6xl hero → 48px h1, 28px h2 (serif briefing).
    h1: { fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "48px", fontColor: "#0F172A" },
    h2: { fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "28px", fontColor: "#0F172A" },
  },
  buttonsTheme: {
    // Primary stays SOLID (blue, white label, 8.72:1); secondary is a true
    // ghost (transparent, blue hairline) per the premium CTA pairing (WS4-B2).
    primary: {
      bgColor: "#1E40AF", bgHoverColor: "#1A3399", borderColor: "#1E40AF", borderHoverColor: "#1A3399",
      fontColor: "#FFFFFF", fontHoverColor: "#FFFFFF", fontSize: "16px", fontHoverSize: "16px",
      borderRadius: "rounded-none", hoverBorderRadius: "rounded-none",
    },
    secondary: {
      bgColor: "transparent", bgHoverColor: "#1E40AF", borderColor: "#1E40AF", borderHoverColor: "#1E40AF",
      fontColor: "#1E40AF", fontHoverColor: "#FFFFFF", fontSize: "16px", fontHoverSize: "16px",
      borderRadius: "rounded-none", hoverBorderRadius: "rounded-none",
    },
  },
  hyperlinkTheme: { fontColor: "#1E40AF", fontHoverColor: "#1A3399", underline: false },
  formInputTheme: {
    labelFontColor: "#475569", inputFontColor: "#0F172A", backgroundColor: "#FFFFFF",
    border: true, borderColor: "#CBD5E1", borderRadius: "rounded-none", borderStyle: "border-solid",
    borderWidth: "border", borderSides: [], fontFamily: "Inter, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "text-sm", errorColor: "#DC2626",
  },
  // WS4-B2 premium css (paper-wash hero, thin rule, self-hosted faces) — the
  // template file owns the string so the section ids stay next to the doc.
  customCss: webinarCustomCss,
};

// ── Theme lookup ─────────────────────────────────────────────────────

const THEME_PRESETS: Record<WebsiteThemeId, WebsiteThemeStylePreset> = {
  webinar: WEBINAR_PRESET,
  speaker: speakerPreset,
  gala: galaPreset,
  charity: charityPreset,
  workshop: workshopPreset,
  corporate: corporatePreset,
  dinner: dinnerPreset,
  networking: networkingPreset,
  networkingNight: networkingNightPreset,
  summit: summitPreset,
  soiree: soireePreset,
  press: pressPreset,
  productLaunch: productLaunchPreset,
};

export function getThemePreset(themeId: WebsiteThemeId): WebsiteThemeStylePreset {
  return THEME_PRESETS[themeId];
}

/**
 * The canonical design-direction each website theme maps to (shared registry,
 * src/lib/designDirections). Unlike the App/Wallet builders — which derive from
 * a small palette and now source their colours straight from a direction — the
 * website's colours are BAKED into each bespoke CraftJS template doc
 * (event-templates/*.ts hold local hex consts like gala's `GOLD`, referenced
 * ~12× per file). Recolouring to the registry therefore means editing those
 * const blocks under a snapshot/visual guard, NOT flipping this map — and the
 * 13 templates were WCAG-reviewed 2026-07-18, so it's a deliberate follow-up.
 * Canon was largely derived FROM these templates: only `gala` (gold
 * #d4af37→#C9A24B) and `workshop` (grey → the `minimal` direction) actually
 * change; the other 11 already match. This map is the anchor for that pass.
 */
export const WEBSITE_THEME_DIRECTION: Record<WebsiteThemeId, DirectionId> = {
  webinar: "webinar",
  speaker: "speaker",
  gala: "gala",
  charity: "charity",
  workshop: "minimal",
  corporate: "corporate",
  dinner: "dinner",
  networking: "networking",
  networkingNight: "networking-night",
  summit: "summit",
  soiree: "soiree",
  press: "press",
  productLaunch: "product-launch",
};

/** Default theme selected when the configurator opens. */
export const DEFAULT_THEME_ID: WebsiteThemeId = "webinar";

/**
 * The `websiteBuilderDefaults` key each template bakes into its DataInput
 * submit button (`<prefix>_di_btn`). The wizard needs the RESOLVED text twice:
 * as the "leave this empty" placeholder — the admin cannot otherwise know that
 * webinar says "Platz bestätigen" and gala "Couvert reservieren" — and as the
 * value to re-inject when the field is emptied (injectRegistrationFields only
 * ever OVERWRITES; once an override landed, the template's own string is gone).
 *
 * Hand-kept second source, so it cannot drift silently:
 * __tests__/templatesDeclareSubmitText.test.ts generates every theme with an
 * identity `td` and asserts the button's text IS the key below.
 */
export const WEBSITE_THEME_SUBMIT_TEXT_KEY: Record<WebsiteThemeId, string> = {
  webinar: "wbnDataSubmit",
  speaker: "spkDataSubmit",
  gala: "galDataSubmit",
  charity: "chaDataSubmit",
  workshop: "wshDataSubmit",
  corporate: "corpDataSubmit",
  dinner: "dinDataSubmit",
  networking: "netDataSubmit",
  networkingNight: "nwnDataSubmit",
  summit: "smtDataSubmit",
  soiree: "sreDataSubmit",
  press: "prsDataSubmit",
  productLaunch: "plDataSubmit",
};

/** The submit label a template ships with — placeholder AND the value the
 *  wizard re-injects when the admin clears the field. ONE resolution site: two
 *  would let the placeholder promise a label the reset does not deliver. */
export const themeSubmitText = (id: WebsiteThemeId, td: TranslationFn): string =>
  td(WEBSITE_THEME_SUBMIT_TEXT_KEY[id]);

export const WEBSITE_THEMES: WebsiteThemeDefinition[] = [
  {
    id: "webinar",
    nameKey: "themeWebinar",
    descriptionKey: "themeWebinarDescription",
    thumbnailPath: "/images/themes/webinar.jpg",
    vibe: "business",
    galleryCaptionKey: "galleryCaptionWebinar",
    // Unused for templateFn themes (the bespoke doc owns section selection; OPEN
    // registration strips DataInput via removeSectionsByName). Kept to satisfy the
    // required field; ignored by buildHomeJson.
    homeSections: [],
    stylePreset: WEBINAR_PRESET,
    templateFn: webinar,
    appbarFn: webinarAppbar,
    footerFn: webinarFooter,
  },
  {
    id: "speaker",
    nameKey: "themeSpeaker",
    descriptionKey: "themeSpeakerDescription",
    thumbnailPath: "/images/themes/speaker.jpg",
    vibe: "community",
    galleryCaptionKey: "galleryCaptionSpeaker",
    homeSections: [],
    stylePreset: speakerPreset,
    templateFn: speaker,
    appbarFn: speakerAppbar,
    footerFn: speakerFooter,
  },
  {
    id: "charity",
    nameKey: "themeCharity",
    descriptionKey: "themeCharityDescription",
    thumbnailPath: "/images/themes/charity.jpg",
    vibe: "community",
    galleryCaptionKey: "galleryCaptionCharity",
    homeSections: [],
    stylePreset: charityPreset,
    templateFn: charity,
    appbarFn: charityAppbar,
    footerFn: charityFooter,
  },
  {
    id: "workshop",
    nameKey: "themeWorkshop",
    descriptionKey: "themeWorkshopDescription",
    thumbnailPath: "/images/themes/workshop.jpg",
    vibe: "business",
    galleryCaptionKey: "galleryCaptionWorkshop",
    homeSections: [],
    stylePreset: workshopPreset,
    templateFn: workshop,
    appbarFn: workshopAppbar,
    footerFn: workshopFooter,
  },
  {
    id: "corporate",
    nameKey: "themeCorporate",
    descriptionKey: "themeCorporateDescription",
    thumbnailPath: "/images/themes/corporate.jpg",
    vibe: "business",
    galleryCaptionKey: "galleryCaptionCorporate",
    homeSections: [],
    stylePreset: corporatePreset,
    templateFn: corporate,
    appbarFn: corporateAppbar,
    footerFn: corporateFooter,
  },
  {
    id: "dinner",
    nameKey: "themeDinner",
    descriptionKey: "themeDinnerDescription",
    thumbnailPath: "/images/themes/dinner.jpg",
    vibe: "celebration",
    galleryCaptionKey: "galleryCaptionDinner",
    homeSections: [],
    stylePreset: dinnerPreset,
    templateFn: dinner,
    appbarFn: dinnerAppbar,
    footerFn: dinnerFooter,
  },
  {
    id: "networking",
    nameKey: "themeNetworking",
    descriptionKey: "themeNetworkingDescription",
    thumbnailPath: "/images/themes/networking.jpg",
    vibe: "community",
    galleryCaptionKey: "galleryCaptionNetworking",
    homeSections: [],
    stylePreset: networkingPreset,
    templateFn: networking,
    appbarFn: networkingAppbar,
    footerFn: networkingFooter,
  },
  {
    // P9-W4: first premium 2026 template — poster-art hero + customCss motion.
    id: "networkingNight",
    nameKey: "themeNetworkingNight",
    descriptionKey: "themeNetworkingNightDescription",
    thumbnailPath: "/images/themes/networkingNight.jpg",
    vibe: "celebration",
    galleryCaptionKey: "galleryCaptionNetworkingNight",
    isNew: true,
    homeSections: [],
    stylePreset: networkingNightPreset,
    templateFn: networkingNight,
    appbarFn: networkingNightAppbar,
    footerFn: networkingNightFooter,
  },
  {
    // P9-W5: premium 2026 template — editorial paper conference page + customCss motion.
    id: "summit",
    nameKey: "themeSummit",
    descriptionKey: "themeSummitDescription",
    thumbnailPath: "/images/themes/summit.jpg",
    vibe: "business",
    galleryCaptionKey: "galleryCaptionSummit",
    isNew: true,
    homeSections: [],
    stylePreset: summitPreset,
    templateFn: summit,
    appbarFn: summitAppbar,
    footerFn: summitFooter,
  },
  {
    // P9-W5: premium 2026 template — midnight-noir candlelight poster + customCss motion.
    id: "soiree",
    nameKey: "themeSoiree",
    descriptionKey: "themeSoireeDescription",
    thumbnailPath: "/images/themes/soiree.jpg",
    vibe: "celebration",
    galleryCaptionKey: "galleryCaptionSoiree",
    isNew: true,
    homeSections: [],
    stylePreset: soireePreset,
    templateFn: soiree,
    appbarFn: soireeAppbar,
    footerFn: soireeFooter,
  },
  {
    id: "press",
    nameKey: "themePress",
    descriptionKey: "themePressDescription",
    thumbnailPath: "/images/themes/press.jpg",
    vibe: "business",
    galleryCaptionKey: "galleryCaptionPress",
    homeSections: [],
    stylePreset: pressPreset,
    templateFn: press,
    appbarFn: pressAppbar,
    footerFn: pressFooter,
  },
  {
    id: "gala",
    nameKey: "themeGala",
    descriptionKey: "themeGalaDescription",
    thumbnailPath: "/images/themes/gala.jpg",
    vibe: "celebration",
    galleryCaptionKey: "galleryCaptionGala",
    homeSections: [],
    stylePreset: galaPreset,
    templateFn: gala,
    appbarFn: galaAppbar,
    footerFn: galaFooter,
  },
  {
    id: "productLaunch",
    nameKey: "themeProductLaunch",
    descriptionKey: "themeProductLaunchDescription",
    thumbnailPath: "/images/themes/productLaunch.jpg",
    vibe: "business",
    galleryCaptionKey: "galleryCaptionProductLaunch",
    homeSections: [],
    stylePreset: productLaunchPreset,
    templateFn: productLaunch,
    appbarFn: productLaunchAppbar,
    footerFn: productLaunchFooter,
  },
];


/**
 * Remove top-level body sections by displayName from an already-compressed
 * CraftJS document (and clean up their orphaned descendants). Used to drop
 * sections that don't apply to a registration type (e.g. DataInput on OPEN).
 *
 * P9-W2 (fold-LOW-8): buttons that anchor to a REMOVED section ("#"+nodeId —
 * every event template's hero CTA targets its DataInput) are hidden, and the
 * owning section's matching isVisible*Button prop is flipped off, so OPEN
 * events don't ship a dead "register" anchor.
 */
function removeSectionsByName(compressed: string, removeNames: string[]): string {
  type TemplateNode = {
    displayName?: string;
    isCanvas?: boolean;
    nodes?: string[];
    linkedNodes?: Record<string, string>;
    props?: Record<string, unknown>;
    [key: string]: unknown;
  };
  const json: Record<string, TemplateNode> = JSON.parse(
    lz.decompress(lz.decodeBase64(compressed)),
  );
  const bodyKey = Object.keys(json).find(
    (k) => json[k]?.displayName === "body" && json[k]?.isCanvas === true,
  );
  if (!bodyKey) return compressed;

  const body = json[bodyKey];
  const originalNodes: string[] = body.nodes ?? [];
  const nodesToDelete = new Set<string>();

  const collectDescendants = (nodeId: string, visited = new Set<string>()) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    nodesToDelete.add(nodeId);
    const node = json[nodeId];
    if (!node) return;
    for (const childId of node.nodes ?? []) collectDescendants(childId, visited);
    for (const linkedId of Object.values(node.linkedNodes ?? {})) {
      if (typeof linkedId === "string") collectDescendants(linkedId, visited);
    }
  };

  const keepNodes = originalNodes.filter((nodeId) => {
    const name = json[nodeId]?.displayName;
    if (name && removeNames.includes(name)) {
      collectDescendants(nodeId);
      return false;
    }
    return true;
  });

  for (const nodeId of nodesToDelete) delete json[nodeId];
  body.nodes = keepNodes;

  // Dead-anchor fixup: hide surviving buttons whose linkUrl targets a deleted
  // node. linkedNodes slot → the owning section's visibility prop.
  const SLOT_VISIBILITY: Record<string, string> = {
    button: "isVisibleButton",
    buttonOne: "isVisibleSecondaryButton",
    buttonTwo: "isVisiblePrimaryButton",
  };
  const deadAnchors = new Set([...nodesToDelete].map((id) => `#${id}`));
  for (const [nodeId, node] of Object.entries(json)) {
    const linkUrl = node?.props?.linkUrl;
    if (typeof linkUrl !== "string" || !deadAnchors.has(linkUrl)) continue;
    node.props = { ...node.props, isVisible: false };
    // Flip the owning section's matching button-visibility prop too — section
    // components render their buttons from their OWN isVisible* props.
    for (const [, owner] of Object.entries(json)) {
      for (const [slot, linkedId] of Object.entries(owner?.linkedNodes ?? {})) {
        if (linkedId !== nodeId) continue;
        const visProp = SLOT_VISIBILITY[slot];
        if (visProp) owner.props = { ...owner.props, [visProp]: false };
      }
    }
  }

  // Dead-anchor fixup pt.2: AppBar `links[]` and Footer `linkGroups[].links[]`
  // store their links inline (NOT as Button nodes with linkUrl), so the loop
  // above misses them. Drop any nav/footer entry pointing at a deleted section
  // (e.g. press's "Accreditation" → #prs_datainput after the OPEN-reg strip).
  const isDead = (l: unknown): boolean =>
    !!l && typeof (l as { url?: unknown }).url === "string" && deadAnchors.has((l as { url: string }).url);
  for (const node of Object.values(json)) {
    const props = node?.props;
    if (!props) continue;
    if (Array.isArray(props.links)) {
      const kept = props.links.filter((l: unknown) => !isDead(l));
      if (kept.length !== props.links.length) node.props = { ...props, links: kept };
    }
    if (Array.isArray(props.linkGroups)) {
      let changed = false;
      const groups = props.linkGroups.map((g: { links?: unknown[] } | null) => {
        if (!g || !Array.isArray(g.links)) return g;
        const kept = g.links.filter((l: unknown) => !isDead(l));
        if (kept.length !== g.links.length) {
          changed = true;
          return { ...g, links: kept };
        }
        return g;
      });
      if (changed) node.props = { ...node.props, linkGroups: groups };
    }
  }

  return lz.encodeBase64(lz.compress(JSON.stringify(json)));
}

// ── P9-W2: event-fact injection ──────────────────────────────────────
//
// Template copy is static i18n (td keys) — a fresh pick would read like a
// fictional event. Facts are injected as RUNTIME OVERRIDES on top of the
// static copy (decision h: copy stays i18n ×57; the facts are per-event
// values): every template names its hero copy keys `{prefix}HeroHeader` /
// `{prefix}HeroEyebrow`, so a td-wrapper covers section props AND linked
// text children in one move, for every current and future template.

export type WebsiteEventFacts = {
  /** Event name → every `*HeroHeader` key. */
  name?: string;
  /** "12. Juni 2026 · Berlin"-style line → every `*HeroEyebrow` key. */
  dateLine?: string;
  /** Event start (ISO) → Countdown sections' countdownTargetDate. */
  startIso?: string;
  /** Google-Maps URL for the real venue → footer "Venue" links (W4). */
  mapsUrl?: string;
  /** Google-Calendar template URL with real name/dates → footer links (W4). */
  calendarUrl?: string;
  /** Organizer company name → footer copyrightCompanyName (W4). */
  companyName?: string;
  /** Event has program items → Agenda sections generate program-bound (W3a). */
  hasProgramItems?: boolean;
  /** Event has speakers → Speaker sections generate speaker-bound (W3a). */
  hasSpeakers?: boolean;
};

/** Suffix-keyed td wrapper — falls back to the static copy per missing fact. */
export function withEventFacts(td: TranslationFn, facts?: WebsiteEventFacts): TranslationFn {
  if (!facts || (!facts.name && !facts.dateLine)) return td;
  return (key: string) => {
    if (facts.name && key.endsWith("HeroHeader")) return facts.name;
    if (facts.dateLine && key.endsWith("HeroEyebrow")) return facts.dateLine;
    return td(key);
  };
}

/**
 * Bind Agenda/Speaker sections to the event's program manager at GENERATION
 * time when the event already HAS program items / speakers (audit W3a —
 * "bindings-first"): stamps the marker prop the panel toggle would stamp
 * (`programBinding` / `speakersBinding`) and REMOVES the template's sample
 * rows (the refresher keeps manual children, so leaving them would mix fake
 * rows with real ones). On first editor mount — which the wizard now lands in
 * directly (W5) — ProgramBindingRefresher generates the `generatedFrom`-tagged
 * children from the real program via the audited path. Events WITHOUT program
 * data keep the sample rows (translated content, W3b) and the publish
 * preflight keeps nudging.
 */
function injectProgramBindings(
  compressed: string,
  facts?: WebsiteEventFacts,
): string {
  if (!facts?.hasProgramItems && !facts?.hasSpeakers) return compressed;
  type TemplateNode = {
    type?: { resolvedName?: string };
    props?: Record<string, unknown>;
    nodes?: string[];
    linkedNodes?: Record<string, string>;
    [key: string]: unknown;
  };
  const json: Record<string, TemplateNode> = JSON.parse(
    lz.decompress(lz.decodeBase64(compressed)),
  );
  const deleteDescendants = (nodeId: string, visited = new Set<string>()) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    const node = json[nodeId];
    if (!node) return;
    for (const childId of node.nodes ?? []) deleteDescendants(childId, visited);
    for (const linkedId of Object.values(node.linkedNodes ?? {})) {
      if (typeof linkedId === "string") deleteDescendants(linkedId, visited);
    }
    delete json[nodeId];
  };
  let touched = false;
  for (const node of Object.values(json)) {
    const name = node?.type?.resolvedName;
    const bindProp =
      name === "Agenda" && facts.hasProgramItems
        ? { key: "programBinding", value: PROGRAM_BINDING_KEY }
        : name === "Speaker" && facts.hasSpeakers
          ? { key: "speakersBinding", value: SPEAKERS_BINDING_KEY }
          : null;
    if (!bindProp) continue;
    node.props = { ...node.props, [bindProp.key]: bindProp.value };
    for (const childId of node.nodes ?? []) deleteDescendants(childId);
    node.nodes = [];
    touched = true;
  }
  return touched ? lz.encodeBase64(lz.compress(JSON.stringify(json))) : compressed;
}

/** Section-prop → CountdownChild-prop mapping. Must mirror the JSX in BOTH
 *  sections/Countdown/Countdown.tsx AND the runtime's countdown.tsx — the
 *  pre-created child must be byte-equivalent to what the engine itself would
 *  materialize from that JSX. */
const COUNTDOWN_CHILD_PROP_MAP: ReadonlyArray<readonly [string, string]> = [
  ["isVisiblCountdown", "isVisible"],
  ["countdownRounded", "rounded"],
  ["countdownFontTypeDate", "fontTypeDate"],
  ["countdownFontColorDate", "fontColorDate"],
  ["countdownSizeDate", "sizeDate"],
  ["countdownFontType", "fontType"],
  ["countdownFontColor", "fontColor"],
  ["countdownSize", "size"],
  ["countdownBorderColor", "borderColor"],
];

/**
 * Countdown fact-binding at generation time (audit G7/W4). Two jobs per
 * Countdown section, ALWAYS run (inert without facts):
 * 1. Bake `countdownTargetDate` when the start is known (runtime fallback for
 *    sites published without an editor visit).
 * 2. PRE-CREATE the linked `CountdownChild` node with `targetDate` baked and
 *    `eventBindings: { targetDate: "event.start" }`. The engine applies JSX
 *    props to a linked child only at CREATION — a child materialized by the
 *    editor's first commit (before the section's date effect ran) froze
 *    `targetDate: null` forever and published a dead countdown. With the
 *    child pre-created and BOUND, EventBindingRefresher re-bakes the date on
 *    every editor load and the published runtime renders the stored value.
 * Pre-existing sites are NOT migrated (a manual date must never be
 * clobbered) — organizers heal via the countdown's event-date binding toggle.
 */
function injectCountdownBindings(compressed: string, startIso?: string): string {
  type TemplateNode = {
    type?: { resolvedName?: string };
    props?: Record<string, unknown>;
    linkedNodes?: Record<string, string>;
    [key: string]: unknown;
  };
  const json: Record<string, TemplateNode> = JSON.parse(
    lz.decompress(lz.decodeBase64(compressed)),
  );
  let touched = false;
  for (const [nodeId, node] of Object.entries(json)) {
    if (node?.type?.resolvedName !== "Countdown") continue;
    const sectionProps = node.props ?? {};
    if (startIso) {
      node.props = { ...sectionProps, countdownTargetDate: startIso };
    }
    if (!node.linkedNodes?.countdown) {
      const childId = `${nodeId}_cdch`;
      const childProps: Record<string, unknown> = {
        targetDate: startIso ?? null,
        eventBindings: { targetDate: "event.start" },
      };
      for (const [from, to] of COUNTDOWN_CHILD_PROP_MAP) {
        if (sectionProps[from] !== undefined) childProps[to] = sectionProps[from];
      }
      json[childId] = {
        type: { resolvedName: "CountdownChild" },
        isCanvas: false,
        props: childProps,
        displayName: "CountdownChild",
        custom: {},
        parent: nodeId,
        hidden: false,
        nodes: [],
        linkedNodes: {},
      };
      node.linkedNodes = { ...(node.linkedNodes ?? {}), countdown: childId };
    }
    touched = true;
  }
  return touched ? lz.encodeBase64(lz.compress(JSON.stringify(json))) : compressed;
}

/**
 * Seed Cvent-style EVENT-FACT BINDINGS on every Hero's text children at
 * GENERATION time (new sites only — saved sites are never rewritten): the
 * eyebrow child (linked slot `headline`) binds to `event.dateLine`, the
 * title child (`header`) to `event.name`. withEventFacts already baked the
 * initial values into the copy, so the bound chip shows real data
 * immediately; from then on EventBindingRefresher re-bakes on every editor
 * load (bake-at-save — the published site updates with the next save). The
 * convention rides the SAME hero anatomy withEventFacts keys on (every
 * template's Hero links `headline`/`header`), so all current and future
 * templates are covered without per-factory edits. Without a mounted event
 * (org-template editors) the seeds are inert.
 */
function injectHeroTextBindings(compressed: string): string {
  type TemplateNode = {
    type?: { resolvedName?: string };
    props?: Record<string, unknown>;
    linkedNodes?: Record<string, string>;
    [key: string]: unknown;
  };
  const json: Record<string, TemplateNode> = JSON.parse(
    lz.decompress(lz.decodeBase64(compressed)),
  );
  const SLOT_FACTS: Record<string, string> = {
    headline: "event.dateLine",
    header: "event.name",
  };
  let touched = false;
  for (const node of Object.values(json)) {
    if (node?.type?.resolvedName !== "Hero") continue;
    for (const [slot, fact] of Object.entries(SLOT_FACTS)) {
      const childId = node.linkedNodes?.[slot];
      const child = childId ? json[childId] : undefined;
      if (!child) continue;
      const bindings =
        (child.props?.eventBindings as Record<string, unknown> | undefined) ?? {};
      child.props = { ...child.props, eventBindings: { ...bindings, text: fact } };
      touched = true;
    }
  }
  return touched ? lz.encodeBase64(lz.compress(JSON.stringify(json))) : compressed;
}

// ── C-2: registration-field injection ────────────────────────────────
//
// The registration wizard step writes the admin's field picks into the
// template's DataInput form. The injector OWNS FieldsGroup 1 wholesale: the
// bespoke event templates ship it with speculative sample fields (e.g.
// webinar's company/role, bound to variables the event may not even have),
// so once the step applies a selection the group is REPLACED with one
// fi()-shaped FormInputChild per picked field — WYSIWYG with the step UI,
// and trivially idempotent. Flows that never apply the step (fast-path
// create, OPEN events, the template variant) keep the samples as designed.
// Scope note: this targets the DataInput ("Abfrage") section only; the
// /register page's account-creation Register section keeps its fixed fields.

/** One picked registration field: the guest field + the required override. */
export type RegistrationFieldWidth = "full" | "half";

// The default consent rows moved to `@/lib/website/registrationConsents`
// (one source for sections, templates and both wizards); re-exported here so
// the existing importers keep working.
export {
  defaultRegistrationConsents,
  type RegistrationConsent,
} from "@/lib/website/registrationConsents";

/** One "only show when …" sentence row (AND-joined). `field` uses the stored
 *  key shape the visibility evaluator expects: "additionalFields.<name>" for
 *  custom fields, the bare name for built-ins. */
export type RegistrationVisibilityRow = {
  field: string;
  operator: "=" | "≠";
  value: string;
};

export type RegistrationFieldSelection = {
  name: string;
  required: boolean;
  /** Admin's custom form label; empty/absent = the field's own label. */
  label?: string;
  /** Form-row width; absent = "full" (matches the templates' own fields). */
  width?: RegistrationFieldWidth;
  /** Lower/upper bound overrides (FormInput minValue/maxValue, type-aware). */
  min?: string;
  max?: string;
  /** AND-joined visibility rows — composed with the acceptance gate. */
  visibilityCondition?: RegistrationVisibilityRow[];
  /** 0-based form step ("pages" mode). Absent = 0. Clamped/compacted by
   *  normalizeRegistrationSteps — max 5 steps (engine pool fields-0..4). */
  step?: number;
};

export const MAX_REGISTRATION_STEPS = 5;

/**
 * Single source of truth for the step model: clamp every entry's step to
 * [0, 4], compact the used values to contiguous 0..N-1 (numeric order kept,
 * array order within a step kept). Pure; wizard, Velo and injector share it.
 */
export function normalizeRegistrationSteps(
  selection: RegistrationFieldSelection[],
): {
  selection: RegistrationFieldSelection[];
  stepCount: number;
  /** The step indices that were OCCUPIED before compaction, ascending — the
   *  remap read backwards. Anything index-keyed by step on the outside
   *  (stepLabels) has to be re-indexed through this or its entries land on
   *  the wrong step the moment a step loses its last field. */
  usedSteps: number[];
} {
  const clamped = selection.map((s) => ({
    ...s,
    step: Math.min(
      MAX_REGISTRATION_STEPS - 1,
      Math.max(0, Math.trunc(s.step ?? 0)),
    ),
  }));
  const used = [...new Set(clamped.map((s) => s.step))].sort((a, b) => a - b);
  const remap = new Map(used.map((v, i) => [v, i]));
  const compacted = clamped.map((s) => {
    const step = remap.get(s.step) ?? 0;
    return step === 0 ? { ...s, step: undefined } : { ...s, step };
  });
  return { selection: compacted, stepCount: Math.max(1, used.length), usedSteps: used };
}

// The public-registration field funnel now lives in
// `@/lib/website/registrationFieldCatalog` so surfaces outside the builder (the
// custom-website AI prompt) can reach it without pulling this module's
// `lzutf8` + 13 event-template import graph into their chunk. Re-exported so
// every existing consumer keeps its import path. A copy would drift; this is a
// move.
export { INJECTABLE_FIELD_DATA_TYPES, getInjectableRegistrationFields };

/**
 * The step's starting suggestion — mirrors what the templates ship in their
 * first fields group (first/last half-width, email full). Just a default:
 * every row is editable and removable like any other.
 */
export const DEFAULT_REGISTRATION_SELECTION: RegistrationFieldSelection[] = [
  { name: "firstName", required: true, width: "half" },
  { name: "lastName", required: true, width: "half" },
  { name: "email", required: true },
];

/** Built-in guest fields have i18n labels in websiteBuilderDefaults. */
const BUILTIN_FIELD_LABEL_KEYS: Record<string, string> = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  phone: "phoneNumber",
};

export function getRegistrationFieldLabel(
  field: Field,
  td: TranslationFn,
  /** "guest-fields" translator — canonical labels for built-in guest fields
   *  (same convention as ConditionRow); optional for legacy callers. */
  tFields?: TranslationFn,
): string {
  if (!field.additionalField) {
    const builtinKey = BUILTIN_FIELD_LABEL_KEYS[field.name];
    if (builtinKey) {
      try {
        return td(builtinKey);
      } catch {
        /* missing key — fall through */
      }
    }
    if (tFields) {
      try {
        const translated = tFields(field.name);
        if (translated?.trim()) return translated;
      } catch {
        /* missing key — fall through */
      }
    }
  }
  // Never return an empty label — a blank row is unusable.
  return field.translation?.trim() || field.name?.trim() || field.fieldId || "—";
}

const INJECTED_FIELD_MARKER = "injectedRegistrationField";

/**
 * Write the picked registration fields into a page's DataInput (FieldsGroup
 * with `groupIndex === 1`, replacing everything in it — see the block
 * comment above). Compressed-in/compressed-out like injectCountdownTarget;
 * pages without a DataInput (OPEN homes, agenda, contact, auth pages) pass
 * through untouched. Idempotent, so re-applying after a selection change or
 * a draft rehydrate never duplicates fields — and MUTATING the current page
 * (instead of a full regeneration) preserves the admin's section edits from
 * the earlier wizard step.
 */
export type RegistrationInjectionEntry = {
  field: Field;
  required: boolean;
  /** Custom label override (trimmed non-empty wins over the field label). */
  label?: string;
  /** "full" (default, col-span-12 like the templates) or "half" (col-span-6). */
  width?: RegistrationFieldWidth;
  /** Bound overrides → FormInput minValue/maxValue. */
  min?: string;
  max?: string;
  /** Admin visibility rows — ANDed with the acceptance gate. */
  visibilityCondition?: RegistrationVisibilityRow[];
  /** 0-based form step (see normalizeRegistrationSteps). */
  step?: number;
};

/**
 * The injected field's visibilityFilter: the standard acceptance gate
 * (state = ACCEPTED — what the DataInput invariant stamps on ordinary
 * fields) AND the admin's sentence rows, all in one AND group.
 */
export function buildRegistrationVisibilityFilter(
  rows?: RegistrationVisibilityRow[],
): typeof ACCEPTANCE_VISIBILITY_FILTER {
  const adminRows = (rows ?? []).filter((r) => r.field && r.value !== "");
  if (adminRows.length === 0) return ACCEPTANCE_VISIBILITY_FILTER;
  return {
    type: "&",
    simpleFilters: [],
    advancedFilters: [
      {
        type: "&",
        simpleFilters: [
          { field: "state", operator: "=", value: "ACCEPTED" },
          ...adminRows.map((r) => ({
            field: r.field,
            operator: r.operator,
            value: r.value,
          })),
        ],
      },
    ],
  };
}

type TemplateNode = {
  type?: { resolvedName?: string };
  props?: Record<string, unknown>;
  parent?: string;
  nodes?: string[];
  linkedNodes?: Record<string, string>;
  [key: string]: unknown;
};

/** A non-fields form step: companion registration or treatment booking.
 *  Rendered by the engine's section pool (companions / booking-0..2 — the
 *  DataInput mounts those linked Elements lazily with sane defaults). */
export type RegistrationSpecialStep = {
  kind: "companions" | "booking";
  /** Step-pill name; blank = number-only pill. */
  label?: string;
};

export type RegistrationInjectionOptions = {
  /** Consent checkboxes — when given, the wizard owns the checkboxes node. */
  consents?: RegistrationConsent[];
  /** Submit-button label override; blank falls back to `submitTextFallback`,
   *  and without that one to the template's own text. */
  submitText?: string;
  /**
   * The template's own submit label (themeSubmitText), used when `submitText`
   * is blank. Needed because this injector only ever OVERWRITES: on a document
   * that already carries an override, the template's string is gone, so
   * "blank = leave it alone" silently means "keep the last thing you typed".
   * The wizard passes it on EVERY call so all five injection paths obey one
   * rule — clearing the field really does restore the template.
   */
  submitTextFallback?: string;
  /** Per-step names for multi-step forms (index = normalized step). Blank
   *  entries render as number-only pills. */
  stepLabels?: string[];
  /**
   * Extra steps appended AFTER the field steps. When provided (even empty),
   * the wizard owns the section pool: the template's own non-fields sections
   * are dropped and only these appear. Caps: 1× companions, 3× booking,
   * total steps ≤ MAX_REGISTRATION_STEPS.
   */
  specialSteps?: RegistrationSpecialStep[];
};

export function injectRegistrationFields(
  compressed: string,
  fields: RegistrationInjectionEntry[],
  td: TranslationFn,
  /** "guest-fields" translator for built-in field labels (optional). */
  tFields?: TranslationFn,
  opts?: RegistrationInjectionOptions,
): string {
  let json: Record<string, TemplateNode>;
  try {
    json = JSON.parse(lz.decompress(lz.decodeBase64(compressed)));
  } catch {
    return compressed;
  }

  const dataInputId = Object.keys(json).find(
    (id) => json[id]?.type?.resolvedName === "DataInput",
  );
  if (!dataInputId) return compressed;
  const di = json[dataInputId];

  // Prefer the DataInput's linked slots; fall back to a groupIndex scan.
  const groupIdOf = (gi: number): string | undefined =>
    di.linkedNodes?.[`fieldsGroup${gi}`] ??
    Object.keys(json).find((id) => {
      const n = json[id];
      return (
        n?.type?.resolvedName === "FieldsGroup" &&
        n.parent === dataInputId &&
        n.props?.groupIndex === gi
      );
    });

  const groupId = groupIdOf(0);
  const group = groupId ? json[groupId] : undefined;
  if (!groupId || !group) return compressed;

  // The step owns the WHOLE form: every fields group is cleared (template
  // samples, template name/email defaults, previous injections) — the
  // selection is the single source of truth, then lives in fields-0.
  for (let gi = 0; gi < 5; gi++) {
    const gid = groupIdOf(gi);
    const g = gid ? json[gid] : undefined;
    if (!g) continue;
    for (const id of g.nodes ?? []) delete json[id];
    g.nodes = [];
    g.props = { ...g.props, fieldCount: 0, isVisible: false };
  }

  // Normalize the step model (clamp/compact) — entries land in the fields
  // group of their step; global index keeps node ids unique across groups.
  const normalizedEntries = (() => {
    const selectionShape = fields.map((f) => ({ name: f.field.name, required: f.required, step: f.step }));
    const { selection: normalized } = normalizeRegistrationSteps(selectionShape);
    return fields.map((f, i) => ({ ...f, step: normalized[i].step ?? 0 }));
  })();
  const stepCount = Math.max(1, ...normalizedEntries.map((f) => f.step + 1));

  const idsPerStep: string[][] = Array.from({ length: stepCount }, () => []);
  normalizedEntries.forEach(({ field, required, label, width, min, max, visibilityCondition, step }, i) => {
    if (!INJECTABLE_FIELD_DATA_TYPES.has(field.dataType)) return; // never inject broken
    const stepGroupId = step === 0 ? groupId : groupIdOf(step);
    if (!stepGroupId || !json[stepGroupId]) return;
    const nodeId = `RegF_${i}_${field.name.replace(/[^A-Za-z0-9_]/g, "_")}`;
    json[nodeId] = fi(stepGroupId, {
      label: label?.trim() || getRegistrationFieldLabel(field, td, tFields),
      overwriteType: field.dataType,
      requiredField: required,
      inputFieldVariable: field.name,
      // The templates' own fields are col-span-12 in the 12-col FieldsGroup
      // grid — "full" mirrors them, "half" puts two fields per row.
      gridArea: width === "half" ? "col-span-6" : "col-span-12",
      changeable: true,
      ...(min?.trim() ? { minValue: min.trim() } : {}),
      ...(max?.trim() ? { maxValue: max.trim() } : {}),
      // Acceptance gate (what the DataInput invariant stamps anyway) AND the
      // admin's "only show when …" rows.
      visibilityFilter: buildRegistrationVisibilityFilter(visibilityCondition),
      [INJECTED_FIELD_MARKER]: true,
    }) as unknown as TemplateNode;
    idsPerStep[step].push(nodeId);
  });

  for (let step = 0; step < stepCount; step++) {
    const gid = step === 0 ? groupId : groupIdOf(step);
    const g = gid ? json[gid] : undefined;
    if (!g) continue;
    g.nodes = idsPerStep[step];
    g.props = {
      ...g.props,
      fieldCount: idsPerStep[step].length,
      // Serialized doc shows step 0 (template convention: later groups ship
      // hidden; DataInput's step effect drives visibility at runtime).
      isVisible: step === 0 && idsPerStep[step].length > 0,
    };
  }

  // AUDIT 2026-08-14 — re-plant the participation question.
  //
  // The clear-and-rebuild above owns the field groups outright, so the
  // template's acceptance node is deleted along with everything else, and the
  // re-injection cannot bring it back: GUEST_STATUS is excluded from
  // INJECTABLE_FIELD_DATA_TYPES on purpose (the visitor-side FormInput renders
  // nothing for it — DataInputAcceptanceField draws the real widget).
  //
  // Without this the wizard ships a form whose every field is gated on
  // `state = ACCEPTED` and which contains no control to answer it. The runtime
  // then falls back to submitting under `acceptanceState`, a key the worker
  // drops silently, so the Zusage is discarded: no state written, no
  // automation, nothing in the guest's history.
  //
  // No `visibilityFilter` — gating the question on the answer makes it delete
  // declines server-side. Same exclusion `buildFormInputProps` makes.
  // Idempotent by construction: fixed id, overwritten on every run.
  if (groupId && json[groupId]) {
    const accId = "RegF_acceptance";
    json[accId] = acceptanceFi(groupId, td("acceptanceState")) as unknown as TemplateNode;
    const g0 = json[groupId]!;
    g0.nodes = [accId, ...(g0.nodes ?? []).filter((n) => n !== accId)];
    g0.props = {
      ...g0.props,
      fieldCount: g0.nodes.length,
      isVisible: true,
    };
  }

  // Consents (checkboxes node) — wizard-owned when provided.
  if (opts?.consents) {
    const checkboxesId = di.linkedNodes?.checkboxes;
    const checkboxesNode = checkboxesId ? json[checkboxesId] : undefined;
    if (checkboxesNode) {
      checkboxesNode.props = {
        ...checkboxesNode.props,
        // toCheckboxItems drops an emailOptIn binding from a REQUIRED row
        // (GDPR Art. 7(4)): the row stays required, it just stores nothing.
        checkboxes: toCheckboxItems(
          opts.consents
            .filter((c) => c.text.trim())
            .map((c) => ({ ...c, text: c.text.trim() })),
        ),
      };
    }
  }

  // Submit-button label: the override, else the template default the caller
  // named. Neither given (a caller that does not own the form) leaves the
  // template's own text in place.
  const submitLabel = opts?.submitText?.trim() || opts?.submitTextFallback?.trim();
  if (submitLabel) {
    const buttonId = di.linkedNodes?.button;
    const buttonNode = buttonId ? json[buttonId] : undefined;
    if (buttonNode) {
      buttonNode.props = { ...buttonNode.props, text: submitLabel };
    }
  }

  // Step model.
  // - Single fields step, NO wizard-owned specials: keep the proven collapse
  //   (template's first label survives, template non-fields sections stay).
  // - Otherwise BUILD the model: one fields group per step, then the
  //   wizard's special steps (companions / booking-N — the engine's section
  //   pool renders them). When opts.specialSteps is provided the wizard owns
  //   the pool outright: template non-fields sections are dropped. Pages
  //   mode is forced on (four templates ship multiStepEnabled:false).
  if (di.props) {
    const overviewId = di.linkedNodes?.overviewGroup;
    const overview = overviewId ? json[overviewId] : undefined;
    // Cap specials: 1× companions, 3× booking, total ≤ MAX steps.
    const specials = (opts?.specialSteps ?? []).filter(
      (s, i, all) =>
        (s.kind === "companions"
          ? all.findIndex((x) => x.kind === "companions") === i
          : all.filter((x, j) => x.kind === "booking" && j < i).length < 3),
    ).slice(0, Math.max(0, MAX_REGISTRATION_STEPS - stepCount));
    const totalSteps = stepCount + specials.length;

    if (totalSteps <= 1 && opts?.specialSteps === undefined) {
      const collapsed = collapseStepModel(di.props.stepSections, di.props.stepLabels);
      if (collapsed) {
        const labels = collapsed.stepLabels
          ? opts?.stepLabels?.[0]?.trim()
            ? [opts.stepLabels[0].trim(), ...collapsed.stepLabels.slice(1)]
            : collapsed.stepLabels
          : undefined;
        di.props = {
          ...di.props,
          stepSections: collapsed.stepSections,
          ...(labels ? { stepLabels: labels } : {}),
        };
        if (overview?.props?.stepSections) {
          overview.props = { ...overview.props, stepSections: collapsed.stepSections };
        }
      }
    } else {
      // Template non-fields sections survive ONLY while the wizard doesn't
      // own the pool (legacy multi-step callers without specialSteps).
      const templateNonFields =
        opts?.specialSteps === undefined
          ? (Array.isArray(di.props.stepSections) ? di.props.stepSections : [])
              .flat()
              .filter((s) => !(typeof s === "string" && s.startsWith("fields-")))
          : [];
      let bookingIdx = 0;
      const specialSections = specials.map((s) =>
        s.kind === "companions" ? ["companions"] : [`booking-${bookingIdx++}`],
      );
      const stepSections = [
        ...Array.from({ length: stepCount }, (_, k) =>
          k === stepCount - 1 ? [`fields-${k}`, ...templateNonFields] : [`fields-${k}`],
        ),
        ...specialSections,
      ];
      const templateFirstLabel = Array.isArray(di.props.stepLabels)
        ? di.props.stepLabels[0]
        : undefined;
      const stepLabels = [
        ...Array.from({ length: stepCount }, (_, k) => {
          const custom = opts?.stepLabels?.[k]?.trim();
          if (custom) return custom;
          return k === 0 && templateFirstLabel ? templateFirstLabel : "";
        }),
        ...specials.map((s) => s.label?.trim() ?? ""),
      ];
      const multi = stepSections.length > 1;
      di.props = {
        ...di.props,
        stepSections,
        stepLabels,
        ...(multi ? { multiStepEnabled: true, multiStepMode: "pages" } : {}),
      };
      if (overview?.props) {
        overview.props = {
          ...overview.props,
          stepSections,
          stepLabels,
          ...(multi ? { multiStepEnabled: true } : {}),
        };
      }
    }
  }

  return lz.encodeBase64(lz.compress(JSON.stringify(json)));
}

/**
 * Rewrite a DataInput stepSections/stepLabels pair for the wizard-owned
 * form: strip every `fields-*` entry, put `fields-0` first in step 0, and
 * drop EVERY step that ends up empty — including the templates' junk empty
 * trailing step (it rendered as a bare "Übersicht" pill carrying nothing but
 * the checkboxes + submit that already show on the last real step; the ACTUAL
 * summary page is the auto overview/verification pill DataInput appends).
 * Labels stay index-aligned with their kept steps. Null when the props carry
 * no step model.
 */
function collapseStepModel(
  stepsRaw: unknown,
  labelsRaw: unknown,
): { stepSections: unknown[][]; stepLabels?: unknown[] } | null {
  if (!Array.isArray(stepsRaw) || stepsRaw.length === 0) return null;
  const labels = Array.isArray(labelsRaw) ? labelsRaw : null;
  const stepSections: unknown[][] = [];
  const stepLabels: unknown[] = [];
  stepsRaw.forEach((step, i) => {
    if (!Array.isArray(step)) return;
    const rest = step.filter(
      (s) => !(typeof s === "string" && s.startsWith("fields-")),
    );
    if (i !== 0 && rest.length === 0) return; // empty step → gone
    stepSections.push(i === 0 ? ["fields-0", ...rest] : rest);
    if (labels) stepLabels.push(labels[i]); // aligned, holes preserved
  });
  return { stepSections, stepLabels: labels ? stepLabels : undefined };
}

/**
 * Registration-step focus view: reduce an (uncompressed) craft document to
 * ONLY its DataInput section — ROOT keeps just that one child, every node
 * outside the section's subtree is dropped. Returns null when the document
 * has no DataInput (caller falls back to the full page).
 */
export function extractDataInputSubtree(
  json: Record<string, TemplateNode>,
  /**
   * Extra DataInput props merged into the EXTRACTED COPY only (the node is
   * cloned) — the wizard passes `{ initialStep }` so the interactive
   * prototype opens on the step being edited. The source pages never carry
   * these, so nothing can leak into the published site.
   */
  previewProps?: Record<string, unknown>,
): Record<string, TemplateNode> | null {
  const dataInputId = Object.keys(json).find(
    (id) => json[id]?.type?.resolvedName === "DataInput",
  );
  if (!dataInputId || !json.ROOT) return null;

  // Collect the DataInput subtree (child nodes + linked slots, recursively).
  const keep = new Set<string>();
  const queue = [dataInputId];
  while (queue.length > 0) {
    const id = queue.pop()!;
    if (keep.has(id) || !json[id]) continue;
    keep.add(id);
    queue.push(...(json[id].nodes ?? []));
    queue.push(...Object.values(json[id].linkedNodes ?? {}));
  }

  const out: Record<string, TemplateNode> = {
    ROOT: { ...json.ROOT, nodes: [dataInputId], linkedNodes: {} },
  };
  for (const id of keep) {
    out[id] =
      id === dataInputId
        ? {
            ...json[id],
            parent: "ROOT",
            ...(previewProps
              ? { props: { ...(json[id].props ?? {}), ...previewProps } }
              : {}),
          }
        : json[id];
  }
  return out;
}

/** i18n key of the friendly "why these pages are pre-included" sentence
 *  (inferred from the event's registration setup). */
export function regTypeConfirmKey(registrationType?: RegistrationType): string {
  switch (registrationType) {
    case RegistrationType.INVITATION:
      return "regTypeConfirmInvitation";
    case RegistrationType.LOCKED:
      return "regTypeConfirmLocked";
    default:
      return "regTypeConfirmOpen";
  }
}

/**
 * Build the home-page websiteJson for a theme from its bespoke CraftJS
 * document (`templateFn`). OPEN registration drops the DataInput section.
 */
function buildHomeJson(
  theme: WebsiteThemeDefinition,
  td: TranslationFn,
  registrationType?: RegistrationType,
  eventFacts?: WebsiteEventFacts,
): string {
  // P9-W2: facts override the static copy at generate time (td wrapper).
  let json = theme.templateFn(withEventFacts(td, eventFacts));
  // OPEN registration collects no attendee data → drop the DataInput section.
  if (registrationType === RegistrationType.OPEN) {
    json = removeSectionsByName(json, ["DataInput"]);
  }
  // Always seeded (facts or not): the bindings are inert until an event
  // editor mounts, where the refresher bakes the real values.
  json = injectCountdownBindings(json, eventFacts?.startIso);
  json = injectProgramBindings(json, eventFacts);
  json = injectHeroTextBindings(json);
  return json;
}

export function generatePages(
  themeId: WebsiteThemeId,
  t: (key: string) => string,
  td: TranslationFn,
  registrationType?: RegistrationType,
): Page[] {
  const theme = WEBSITE_THEMES.find((th) => th.id === themeId) ?? WEBSITE_THEMES[0];

  // Home page: bespoke template doc (event themes) or filtered welcome (legacy)
  const homePage: Page = {
    pageName: t("home"),
    slug: "/",
    active: true,
    websiteJson: buildHomeJson(theme, td, registrationType),
  };

  return [homePage];
}

export function buildMetaSections(td: TranslationFn) {
  return [appbar(), footer(td)];
}

// ── Page selection helpers ───────────────────────────────────────────

/** Pages auto-included based on event registration type (not user-toggleable) */
export function getAutoPages(registrationType?: RegistrationType): string[] {
  switch (registrationType) {
    case RegistrationType.INVITATION:
      return ["register", "login"];
    case RegistrationType.LOCKED:
      return ["login"];
    // OPEN = public self-registration (worker registration-rules.ts: mode
    // decides instant/request) — the site MUST carry a sign-up page. The
    // home DataInput is auth-gated "edit my data", so without /register an
    // OPEN site has no registration path at all (SOTA audit G1).
    case RegistrationType.OPEN:
      return ["register", "login"];
    default: // unknown (previews, missing event) — no auth pages
      return [];
  }
}

/** Pages the user can toggle on/off */
export const TOGGLEABLE_PAGES = ["agenda", "contact"];

export const PAGE_DEFINITIONS: {
  id: string;
  nameKey: string;
  slugKey: string;
  descriptionKey: string;
  templateFn: (td: TranslationFn) => string;
  isContentPage: boolean; // content pages get nav links; register/login don't
  fixedSlug?: string; // hardcoded slug (login/register are never translated)
}[] = [
  { id: "agenda", nameKey: "agenda", slugKey: "agendaSlug", descriptionKey: "agendaPageDescription", templateFn: agenda, isContentPage: true },
  { id: "contact", nameKey: "contact", slugKey: "contactSlug", descriptionKey: "contactPageDescription", templateFn: contact, isContentPage: true },
  { id: "register", nameKey: "register", slugKey: "", descriptionKey: "registerPageDescription", templateFn: register, isContentPage: false, fixedSlug: "/register" },
  { id: "login", nameKey: "login", slugKey: "", descriptionKey: "loginPageDescription", templateFn: login, isContentPage: false, fixedSlug: "/login" },
];

export function generateAllPages(
  themeId: WebsiteThemeId,
  enabledPageIds: string[],
  t: (key: string) => string,
  td: TranslationFn,
  registrationType?: RegistrationType,
  // P9-W2 (fold-HIGH-1): EVERY call site must pass this — facts must survive
  // theme switches and page toggles, or the fill chips lie.
  eventFacts?: WebsiteEventFacts,
): Page[] {
  const theme = WEBSITE_THEMES.find((th) => th.id === themeId) ?? WEBSITE_THEMES[0];

  const homePage: Page = {
    pageName: t("home"),
    slug: "/",
    active: true,
    websiteJson: buildHomeJson(theme, td, registrationType, eventFacts),
  };

  const hasRegisterPage = enabledPageIds.includes("register");
  const extraPages: Page[] = enabledPageIds
    .map((pageId) => {
      const def = PAGE_DEFINITIONS.find((d) => d.id === pageId);
      if (!def) return null;
      let websiteJson = pageId === "login"
        ? login(td, hasRegisterPage)
        : def.templateFn(td);
      // The /agenda content page carries the same Agenda section as the home —
      // program-bind it under the same condition, or an event WITH a program
      // would publish real sessions on the home and sample rows on /agenda
      // (audit M1).
      if (pageId === "agenda") {
        websiteJson = injectProgramBindings(websiteJson, eventFacts);
      }
      return {
        pageName: t(def.nameKey),
        slug: def.fixedSlug ?? t(def.slugKey),
        active: true,
        websiteJson,
      } as Page;
    })
    .filter((p): p is Page => p !== null);

  return [homePage, ...extraPages];
}

/**
 * Generate the default login and register page templates (always needed for backend storage).
 * These are stored on the WebsiteConfig so the backend can create pages when registrationType changes.
 */
export function generateDefaultAuthTemplates(
  t: (key: string) => string,
  td: TranslationFn,
): { defaultLoginJson: string; defaultRegisterJson: string } {
  // NOTE (audit 2026-07-20): the auth templates' own footer node is the
  // SCAFFOLD WRAPPER (props inert — it renders children only). The footer a
  // visitor actually sees on /login and /register is the SHARED
  // websiteConfig.footer bundle spliced over the "FooterSection" placeholder
  // (WebsiteEditorPage + the runtime's buildRenderablePageJson), and THAT
  // bundle already receives the real event facts via
  // buildMetaSectionsWithLinks → applyFooterEventFacts. Do not "fix" facts
  // here — writes to these templates' footer props are dead.
  const loginDef = PAGE_DEFINITIONS.find((d) => d.id === "login")!;
  const registerDef = PAGE_DEFINITIONS.find((d) => d.id === "register")!;
  return {
    defaultLoginJson: loginDef.templateFn(td),
    defaultRegisterJson: registerDef.templateFn(td),
  };
}

type FooterLink = { label?: unknown; url?: unknown; [k: string]: unknown };
type FooterLinkGroup = { title?: unknown; links?: FooterLink[] };

const MAPS_URL_PREFIX = "https://www.google.com/maps/search/";
const CALENDAR_URL_PREFIX = "https://calendar.google.com/calendar/render";
/** Footer links to pages the wizard never creates — always stripped (G11). */
const DEAD_LEGAL_SLUGS = new Set(["/privacy", "/imprint", "/terms"]);

/**
 * Replace the bespoke footers' hardcoded sample facts with REAL event facts
 * at generation time (audit G7/G11/W4) — honest-drop policy: a link whose
 * real fact is unknown is REMOVED, never left pointing at the template's
 * German sample city / fantasy calendar entry. Replaced links carry an
 * `eventBinding` marker field (ignored by the runtime) so a future
 * footer-refresh pass can find them without index guessing. Legal links
 * (/privacy|/imprint|/terms) are always dropped: those pages never exist.
 * Bake-at-generation contract: values go stale if the event moves — same
 * deliberate limitation as every other bake (see eventBinding.ts header).
 */
function applyFooterEventFacts(
  footerProps: Record<string, unknown>,
  facts: WebsiteEventFacts | undefined,
  hasRegisterPage: boolean,
): void {
  const groups = footerProps.linkGroups as FooterLinkGroup[] | undefined;
  if (Array.isArray(groups)) {
    const mapped = groups
      .map((group) => {
        if (!group || !Array.isArray(group.links)) return group;
        const links = group.links
          .map((link) => {
            const url = typeof link?.url === "string" ? link.url : "";
            if (DEAD_LEGAL_SLUGS.has(url)) return null;
            // Bespoke footers bake /register links ("Tickets", "RSVP") — dead
            // on LOCKED events where no register page exists (audit L3).
            if (url === "/register" && !hasRegisterPage) return null;
            if (url.startsWith(MAPS_URL_PREFIX)) {
              return facts?.mapsUrl
                ? { ...link, url: facts.mapsUrl, eventBinding: "event.mapsUrl" }
                : null;
            }
            if (url.startsWith(CALENDAR_URL_PREFIX)) {
              return facts?.calendarUrl
                ? { ...link, url: facts.calendarUrl, eventBinding: "event.calendarUrl" }
                : null;
            }
            return link;
          })
          .filter((l): l is FooterLink => l !== null);
        return { ...group, links };
      })
      .filter((g) => !g || !Array.isArray(g.links) || g.links.length > 0);
    footerProps.linkGroups = mapped;
  }
  if (facts?.companyName) {
    footerProps.copyrightCompanyName = facts.companyName;
  }
}

export function buildMetaSectionsWithLinks(
  td: TranslationFn,
  t: (key: string) => string,
  enabledPageIds: string[],
  themeId?: WebsiteThemeId,
  eventFacts?: WebsiteEventFacts,
) {
  // Build links for content pages only
  const contentLinks = enabledPageIds
    .map((id) => PAGE_DEFINITIONS.find((d) => d.id === id))
    .filter((d) => d?.isContentPage)
    .map((d) => ({ label: t(d!.nameKey), url: t(d!.slugKey) }));

  // A /register page (auto-included per registration type) gets a nav entry —
  // the templates' header CTA slot is the auth-gated logout button, so without
  // this link an anonymous visitor has no path to the sign-up page (audit G1).
  const registerLinks = enabledPageIds.includes("register")
    ? [{ label: t("register"), url: "/register" }]
    : [];

  // Keep link data mounted, and control visibility when there are no content links.
  const appBarLinks = [{ label: t("home"), url: "/" }, ...contentLinks, ...registerLinks];
  const hasContentLinks = contentLinks.length > 0 || registerLinks.length > 0;

  const theme = themeId ? WEBSITE_THEMES.find((th) => th.id === themeId) : undefined;

  // AppBar: event templates bake their own nav/styling; legacy themes get the
  // shared bar with dynamically-injected content-page links.
  const rawAppBar = JSON.parse(
    lz.decompress(lz.decodeBase64(theme?.appbarFn ? theme.appbarFn(td) : appbar())),
  );
  if (!theme?.appbarFn) {
    rawAppBar.props.isLinksVisible = hasContentLinks;
    rawAppBar.props.links = appBarLinks;
  } else if (contentLinks.length || registerLinks.length) {
    // Event theme bakes its own nav (anchor links to home sections). Still append
    // any enabled toggleable content pages (agenda/contact) + the register page
    // so they're reachable.
    rawAppBar.props.links = [
      ...((rawAppBar.props.links as { label: string; url: string }[] | undefined) ?? []),
      ...contentLinks,
      ...registerLinks,
    ];
    rawAppBar.props.isLinksVisible = true;
  }

  // Footer: same split — bespoke footer (its own link groups) or shared footer
  // with a single dynamically-built group.
  const rawFooter = JSON.parse(
    lz.decompress(lz.decodeBase64(theme?.footerFn ? theme.footerFn(td) : footer(td))),
  );
  if (!theme?.footerFn) {
    rawFooter.props.isLinkGroupsVisible = hasContentLinks;
    rawFooter.props.linkGroups = [{ title: t("company"), links: appBarLinks }];
  } else if (contentLinks.length) {
    // Append enabled content-page links to the bespoke footer so they're reachable.
    rawFooter.props.linkGroups = [
      ...((rawFooter.props.linkGroups as { title: string; links: unknown[] }[] | undefined) ?? []),
      { title: t("company"), links: contentLinks },
    ];
  }
  // Real facts into the footer (maps/calendar/©) + dead-link strip (legal
  // always; /register when no register page) — bespoke AND shared footers.
  applyFooterEventFacts(
    rawFooter.props as Record<string, unknown>,
    eventFacts,
    registerLinks.length > 0,
  );

  // Inject themed logo (only when the theme doesn't bake its own meta sections)
  if (themeId) {
    const preset = THEME_PRESETS[themeId];
    if (preset.logoSrc) {
      if (!theme?.appbarFn) rawAppBar.props.logoSrc = preset.logoSrc;
      if (!theme?.footerFn) rawFooter.props.logoSrc = preset.logoSrc;
    }
  }

  return [
    lz.encodeBase64(lz.compress(JSON.stringify(rawAppBar))),
    lz.encodeBase64(lz.compress(JSON.stringify(rawFooter))),
  ];
}
